import {assertDefined, Values} from '@wix/devzai-utils-common';
import {DoppeDtoAction, doppeDtoActionViewTypeIsDisplayedAsButton} from './doppe-dto-action';
import {DoppeViewerClientServices} from '../../client/doppe-user-app/doppe-viewer-client-services';
import {doppeHideableValueIsVisibleAndNotEqualValue} from './doppe-hideable-value';
import {DoppeActionListItemClickBehaviour} from './doppe-action-list-item-click-behaviour';
import {WixMediaResource} from '@wix/devzai-common-wix';
import {DoppeDtoCouponCodeData} from './doppe-dto-bymo-link';
import {DoppeDtoSpeedDialLinkViewerData} from './doppe-dto-speed-dial-link-viewer-data';
import {DoppeActionWidgetOpeningMode} from './doppe-action-widget-opening-mode';
import {DoppeActionType, doppeActionTypeSupportsInPageView} from '../doppe-action-types/doppe-action-type';
import {doppeActionTypeBanner} from '../../doppe-action-types/doppe-action-type-banner/doppe-action-type-banner';

export const DoppeSearchItemViewerDataType = {
    Link: 'link',
    Action: 'action',
    ActionListItem: 'action-list-item',
} as const;

export type DoppeSearchItemViewerDataType = Values<typeof DoppeSearchItemViewerDataType>;


export type DoppeSearchItemViewerDataLinkData =
    DoppeSearchItemViewerDataBase<typeof DoppeSearchItemViewerDataType.Link>
    & {
    targetUrl: string;
}

export type DoppeSearchItemViewerDataActionData =
    DoppeSearchItemViewerDataBase<typeof DoppeSearchItemViewerDataType.Action>
    & {
    action: DoppeDtoAction<any>;
    isLink: boolean;
    actionIndex: number;
}

export type DoppeSearchItemViewerDataActionListItemData =
    DoppeSearchItemViewerDataBase<typeof DoppeSearchItemViewerDataType.ActionListItem>
    & {
    actionListItem: DoppeDtoAction.ActionListItem<any>;
    isLink: boolean;
    actionIndex: number;
    actionListItemIndex: number;
}

export type DoppeSearchItemViewerDataBase<Type> = {
    type: Type;
};
export type DoppeSearchItemViewerData = DoppeSearchItemViewerDataActionData | DoppeSearchItemViewerDataLinkData | DoppeSearchItemViewerDataActionListItemData;

export interface DoppeSearchItemViewer<DATA extends {} = any> {
    id: string;
    data: DATA;
    image: WixMediaResource | null;
    title: string;
    description?: string;
    couponCodeData?: DoppeDtoCouponCodeData;
    speedDialCode: string | null;
    isVisible: boolean;
}

export function doppeSearchItemViewerIsOfType<T extends DoppeSearchItemViewerData>(
    item: DoppeSearchItemViewer,
    dataType: T['type']
): item is DoppeSearchItemViewer<T> {
    return item.data.type === dataType;
}

export function getTargetUrlFromSearchItem(searchItem: DoppeSearchItemViewer, clientServices: DoppeViewerClientServices) {
    if (doppeSearchItemViewerIsOfType<DoppeSearchItemViewerDataLinkData>(searchItem, DoppeSearchItemViewerDataType.Link)) {
        return searchItem.data.targetUrl;
    } else if (doppeSearchItemViewerIsOfType<DoppeSearchItemViewerDataActionData>(searchItem, DoppeSearchItemViewerDataType.Action)) {
        const action = searchItem.data.action;
        if (searchItem.data.isLink) {
            const actionType = assertDefined(
                clientServices.actionTypesStore.getRegisteredActionType(action.actionTypeId),
                `Action type with id '${action.actionTypeId}' isn't registered`);

            return actionType.resolveMainLink ? actionType.resolveMainLink(action.settings) : null;
        }
    } else if (doppeSearchItemViewerIsOfType<DoppeSearchItemViewerDataActionListItemData>(searchItem, DoppeSearchItemViewerDataType.ActionListItem)) {
        const actionListItem = searchItem.data.actionListItem;
        if (searchItem.data.isLink) {
            return actionListItem.url;
        }
    }

    return null;
}

export function converActionListItemToSearchItemViewer(listItem: DoppeDtoAction.ActionListItem<any>, actionIndex: number, clientServices: DoppeViewerClientServices): DoppeSearchItemViewer<DoppeSearchItemViewerDataActionListItemData> {
    const action = listItem.action;
    const isLinkClickBehaviour = action.settings.itemClickBehaviour === DoppeActionListItemClickBehaviour.OpenLink;

    const image = listItem.image;
    const title = listItem.title;
    const isIndexedInSearch = action.settings.listSearchSettings?.indexItemsInSearch ?? false;

    const actionType = assertDefined(
        clientServices.actionTypesStore.getRegisteredActionType(action.actionTypeId),
        `Action type with id '${action.actionTypeId}' isn't registered`);

    const actionListItemIndex = actionType.resolveListItems ? actionType.resolveListItems(action).findIndex(item => item.itemId === listItem.itemId) ?? -1 : -1;

    const data = {
        actionListItem: listItem,
        type: DoppeSearchItemViewerDataType.ActionListItem,
        isLink: listItem.url !== null && isLinkClickBehaviour,
        actionIndex: actionIndex + 1,
        actionListItemIndex: actionListItemIndex,
    } as DoppeSearchItemViewerDataActionListItemData;

    return {
        id: action.id + listItem.itemId,
        data: data,
        image: image ?? null,
        title: title,
        description: undefined,
        speedDialCode: null,
        isVisible: isIndexedInSearch && action.enabled,
        couponCodeData: actionType.resolveCouponDataFromListItem ? actionType.resolveCouponDataFromListItem(listItem) : undefined,
    }
}


export function converActionToSearchItemViewer(action: DoppeDtoAction<any>, actionIndex: number, clientServices: DoppeViewerClientServices): DoppeSearchItemViewer<DoppeSearchItemViewerDataActionData> {

    const actionType = assertDefined(
        clientServices.actionTypesStore.getRegisteredActionType(action.actionTypeId),
        `Action type with id '${action.actionTypeId}' isn't registered`);

    return converActionToSearchItemViewerForActionType(action, actionIndex, actionType);
}

export function converActionToSearchItemViewerForActionType(action: DoppeDtoAction<any>, actionIndex: number, actionType: DoppeActionType<any>): DoppeSearchItemViewer<DoppeSearchItemViewerDataActionData> {

    const isLinkClickBehaviour = (action.widgetOpeningMode === DoppeActionWidgetOpeningMode.OpenCTA && doppeDtoActionViewTypeIsDisplayedAsButton(action.viewType)) || (!doppeActionTypeSupportsInPageView(actionType) && !actionType.isModalOpener) || actionType.id === doppeActionTypeBanner.id

    const link = actionType.resolveMainLink ? actionType.resolveMainLink(action.settings) : null;
    const couponCodeData = actionType.resolveCouponData ? actionType.resolveCouponData(action.settings) : undefined;
    const image = doppeHideableValueIsVisibleAndNotEqualValue(action.searchSettings.image, null) ? action.searchSettings.image : (doppeHideableValueIsVisibleAndNotEqualValue(action.icon, null) ? action.icon : actionType.resolveMainMedia ? actionType.resolveMainMedia(action.settings) : null);
    const title = doppeHideableValueIsVisibleAndNotEqualValue(action.searchSettings.title, '') ? action.searchSettings.title : action.name;
    const description = doppeHideableValueIsVisibleAndNotEqualValue(action.searchSettings.description, '') ? action.searchSettings.description : (doppeHideableValueIsVisibleAndNotEqualValue(action.callToActionDescription, '') ? action.callToActionDescription : undefined);
    const keyword = doppeHideableValueIsVisibleAndNotEqualValue(action.searchSettings.keyword, '') ? action.searchSettings.keyword : null;
    const isIndexedInSearch = action.searchSettings.indexInSearch;

    const data = {
        action: action,
        type: DoppeSearchItemViewerDataType.Action,
        isLink: link !== null && isLinkClickBehaviour,
        actionIndex: actionIndex + 1,
    }

    return {
        id: action.id,
        data: data,
        image: image ?? null,
        title: title,
        description: description,
        speedDialCode: keyword,
        couponCodeData: couponCodeData,
        isVisible: isIndexedInSearch && (!isLinkClickBehaviour || link !== null) && action.enabled,
    }
}


export function converSpeeddialLinkToSearchItemViewer(link: DoppeDtoSpeedDialLinkViewerData): DoppeSearchItemViewer<DoppeSearchItemViewerDataLinkData> {
    const data = {
        targetUrl: link.targetUrl,
        type: DoppeSearchItemViewerDataType.Link
    }

    return {
        id: link.id,
        data: data,
        image: link.image,
        title: link.title,
        speedDialCode: link.speedDialCode,
        couponCodeData: link.couponCodeData,
        isVisible: link.isVisible,
    }
}

